/**
 * Framing analysis — pure math, no WebGL, no allocations in the hot loop beyond
 * the `visible` id list. Used by the operators every frame and by grading via
 * CameraFrame.framing.
 *
 * Shot size is classified from the apparent height of a standing person
 * (SUBJECT_HEIGHT) at the subject's depth, as a fraction of frame height —
 * the way broadcast crews talk about "how big the people are".
 */
import type { CameraFrame, ShotSize, Vec3, WorldSnapshot } from '../../core/contracts';

export const SUBJECT_HEIGHT = 1.8;
const PLAYER_HALF_WIDTH = 0.3;
const CHEST_Y = 1.0;

export const SHOT_SIZES: readonly ShotSize[] = ['extreme_wide', 'wide', 'medium', 'close', 'extreme_close'];

/** Upper bounds (exclusive) of person-height / frame-height for each size. */
export const SHOT_SIZE_THRESHOLDS = { extreme_wide: 0.08, wide: 0.18, medium: 0.4, close: 0.75 } as const;

/** What an operator aims for when asked for a size (comfortably inside each band). */
export const SHOT_SIZE_TARGET: Record<ShotSize, number> = {
  extreme_wide: 0.05, wide: 0.12, medium: 0.28, close: 0.56, extreme_close: 1.05,
};

export function shotSizeFromFraction(f: number): ShotSize {
  if (f < SHOT_SIZE_THRESHOLDS.extreme_wide) return 'extreme_wide';
  if (f < SHOT_SIZE_THRESHOLDS.wide) return 'wide';
  if (f < SHOT_SIZE_THRESHOLDS.medium) return 'medium';
  if (f < SHOT_SIZE_THRESHOLDS.close) return 'close';
  return 'extreme_close';
}

export function shiftSize(s: ShotSize, steps: number): ShotSize {
  const i = SHOT_SIZES.indexOf(s) + steps;
  return SHOT_SIZES[Math.max(0, Math.min(SHOT_SIZES.length - 1, i))]!;
}

/** Orthonormal camera pose. forward/right/up are unit vectors. */
export interface CameraPose {
  position: Vec3;
  forward: Vec3;
  right: Vec3;
  up: Vec3;
  fovDeg: number;   // vertical
  aspect: number;
  near: number;
  /** Derived: tan(vfov/2), tan(hfov/2). */
  tanV: number;
  tanH: number;
}

/** Build a pose from yaw (radians from +x toward +z), pitch (up positive) and roll. */
export function poseFromAngles(position: Vec3, yaw: number, pitch: number, roll: number, fovDeg: number, aspect: number, near = 0.1, out?: CameraPose): CameraPose {
  const cp = Math.cos(pitch), sp = Math.sin(pitch), cy = Math.cos(yaw), sy = Math.sin(yaw);
  const fx = cp * cy, fy = sp, fz = cp * sy;
  // right = normalize(forward x worldUp) = (-fz, 0, fx)/|..|
  const rl = Math.hypot(fx, fz) || 1e-9;
  let rx = -fz / rl, ry = 0, rz = fx / rl;
  // up = right x forward
  let ux = ry * fz - rz * fy, uy = rz * fx - rx * fz, uz = rx * fy - ry * fx;
  if (roll !== 0) {
    const cr = Math.cos(roll), sr = Math.sin(roll);
    const nrx = rx * cr + ux * sr, nry = ry * cr + uy * sr, nrz = rz * cr + uz * sr;
    ux = ux * cr - rx * sr; uy = uy * cr - ry * sr; uz = uz * cr - rz * sr;
    rx = nrx; ry = nry; rz = nrz;
  }
  const tanV = Math.tan((fovDeg * Math.PI) / 360);
  const o = out ?? ({ position: { x: 0, y: 0, z: 0 }, forward: { x: 0, y: 0, z: 0 }, right: { x: 0, y: 0, z: 0 }, up: { x: 0, y: 0, z: 0 } } as CameraPose);
  o.position.x = position.x; o.position.y = position.y; o.position.z = position.z;
  o.forward.x = fx; o.forward.y = fy; o.forward.z = fz;
  o.right.x = rx; o.right.y = ry; o.right.z = rz;
  o.up.x = ux; o.up.y = uy; o.up.z = uz;
  o.fovDeg = fovDeg; o.aspect = aspect; o.near = near;
  o.tanV = tanV; o.tanH = tanV * aspect;
  return o;
}

/** Yaw/pitch that look from `from` to `to`. */
export function anglesTo(from: Vec3, to: Vec3): { yaw: number; pitch: number } {
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  return { yaw: Math.atan2(dz, dx), pitch: Math.atan2(dy, Math.hypot(dx, dz)) };
}

export function poseLookAt(position: Vec3, target: Vec3, fovDeg: number, aspect: number, near = 0.1): CameraPose {
  const a = anglesTo(position, target);
  return poseFromAngles(position, a.yaw, a.pitch, 0, fovDeg, aspect, near);
}

export interface Projected { x: number; y: number; depth: number }

/**
 * Project a world point to NDC (-1..1 both axes, +y up). Returns depth along the
 * view axis; x/y are meaningless when depth <= near.
 */
export function projectPoint(pose: CameraPose, px: number, py: number, pz: number, out: Projected): Projected {
  const dx = px - pose.position.x, dy = py - pose.position.y, dz = pz - pose.position.z;
  const f = pose.forward, r = pose.right, u = pose.up;
  const depth = dx * f.x + dy * f.y + dz * f.z;
  out.depth = depth;
  if (depth <= 1e-6) { out.x = 99; out.y = 99; return out; }
  out.x = (dx * r.x + dy * r.y + dz * r.z) / (depth * pose.tanH);
  out.y = (dx * u.x + dy * u.y + dz * u.z) / (depth * pose.tanV);
  return out;
}

/** Fraction of frame height a standing person occupies at a given depth. */
export function personFraction(pose: CameraPose, depth: number): number {
  return SUBJECT_HEIGHT / (2 * Math.max(depth, 1e-3) * pose.tanV);
}

/** Vertical fov (deg) that makes a person at `dist` fill `fraction` of frame height. */
export function fovForFraction(fraction: number, dist: number): number {
  return (2 * Math.atan(SUBJECT_HEIGHT / (2 * fraction * Math.max(dist, 0.5))) * 180) / Math.PI;
}

/** Vertical fov (deg) that shows `width` metres horizontally at `dist`. */
export function fovForCoverage(width: number, dist: number, aspect: number): number {
  return (2 * Math.atan(width / aspect / (2 * Math.max(dist, 0.5))) * 180) / Math.PI;
}

export interface FramingInput {
  pose: CameraPose;
  snapshot: WorldSnapshot;
  /** Player id, 'ball', or null (crowd/bench/scenery). */
  subjectId: string | 'ball' | null;
  /** Used for shot size/composition when subjectId is null or not found. */
  subjectPoint?: Vec3;
  /** Operator angular speed in rad/s (pan/tilt), for the steadiness term. */
  angularSpeed?: number;
}

type Framing = CameraFrame['framing'];

interface Box { x0: number; x1: number; y0: number; y1: number; depth: number }

const _a: Projected = { x: 0, y: 0, depth: 0 };
const _b: Projected = { x: 0, y: 0, depth: 0 };

function playerBox(pose: CameraPose, pos: Vec3, out: Box): boolean {
  projectPoint(pose, pos.x, pos.y, pos.z, _a);
  projectPoint(pose, pos.x, pos.y + SUBJECT_HEIGHT, pos.z, _b);
  const depth = Math.min(_a.depth, _b.depth);
  if (depth <= pose.near) return false;
  const hw = PLAYER_HALF_WIDTH / (Math.max(_a.depth, 1e-3) * pose.tanH);
  const cx = (_a.x + _b.x) * 0.5;
  out.x0 = cx - hw; out.x1 = cx + hw;
  out.y0 = Math.min(_a.y, _b.y); out.y1 = Math.max(_a.y, _b.y);
  out.depth = (_a.depth + _b.depth) * 0.5;
  return true;
}

const inFrame = (x: number, y: number, m = 1) => x >= -m && x <= m && y >= -m && y <= m;

const _box: Box = { x0: 0, x1: 0, y0: 0, y1: 0, depth: 0 };
const _sbox: Box = { x0: 0, x1: 0, y0: 0, y1: 0, depth: 0 };
const _p: Projected = { x: 0, y: 0, depth: 0 };

/**
 * Classify what a camera sees. Cost: ~50 point projections + 22 box overlaps.
 */
export function analyzeFraming(input: FramingInput): Framing {
  const { pose, snapshot: s } = input;
  const visible: string[] = [];
  let subjectId = input.subjectId;
  let subjectFound = false;
  let subjectIsPlayer = false;

  // --- visibility of every entity ---------------------------------------------------
  for (let i = 0; i < s.players.length; i++) {
    const pl = s.players[i]!;
    projectPoint(pose, pl.pos.x, pl.pos.y + CHEST_Y, pl.pos.z, _p);
    if (_p.depth > pose.near && inFrame(_p.x, _p.y)) visible.push(pl.id);
    if (pl.id === subjectId) { subjectFound = true; subjectIsPlayer = true; }
  }
  const b = s.ball.pos;
  projectPoint(pose, b.x, b.y, b.z, _p);
  const ballInFrame = _p.depth > pose.near && inFrame(_p.x, _p.y);
  if (ballInFrame) visible.push('ball');
  const ballNdcX = _p.x, ballNdcY = _p.y, ballDepth = _p.depth;
  const r = s.officials.referee;
  projectPoint(pose, r.x, r.y + CHEST_Y, r.z, _p);
  if (_p.depth > pose.near && inFrame(_p.x, _p.y)) visible.push('referee');
  const f = s.focus;
  projectPoint(pose, f.x, f.y, f.z, _p);
  const focusInFrame = _p.depth > pose.near && inFrame(_p.x, _p.y);

  if (subjectId === 'ball') subjectFound = true;
  if (subjectId !== null && !subjectFound) subjectId = null;

  // --- subject geometry -------------------------------------------------------------
  let sx = 0, sy = 0, depth = 1, subjectVisible = true, cutScore = 1, vPenalty = 0, occ = 0;
  let fraction: number;
  if (subjectIsPlayer) {
    const pl = s.players.find((q) => q.id === subjectId)!;
    if (!playerBox(pose, pl.pos, _sbox)) {
      subjectVisible = false;
      depth = Math.max(1, Math.hypot(pl.pos.x - pose.position.x, pl.pos.z - pose.position.z));
    } else {
      depth = _sbox.depth;
      sx = (_sbox.x0 + _sbox.x1) * 0.5;
      sy = (_sbox.y0 + _sbox.y1) * 0.5;
      const headIn = _sbox.y1 <= 1.02;
      const sideIn = _sbox.x0 >= -1 && _sbox.x1 <= 1;
      fraction = personFraction(pose, depth);
      const size = shotSizeFromFraction(fraction);
      const feetMatter = size === 'extreme_wide' || size === 'wide';
      const feetIn = _sbox.y0 >= -1;
      subjectVisible = _sbox.x1 > -1 && _sbox.x0 < 1 && _sbox.y1 > -1 && _sbox.y0 < 1;
      cutScore = (headIn ? 1 : 0.35) * (sideIn ? 1 : 0.55) * (!feetMatter || feetIn ? 1 : 0.6);
      // Close-ups want the eyes on the upper third; wides just want the body away from edges.
      if (size === 'close' || size === 'extreme_close') {
        const headY = _sbox.y0 + (_sbox.y1 - _sbox.y0) * 0.93;
        vPenalty = headY > 0.92 ? (headY - 0.92) * 3 : headY < 0.1 ? (0.1 - headY) * 1.2 : 0;
      } else {
        vPenalty = Math.max(0, Math.abs(sy) - 0.55) * 1.5;
      }
      // Occlusion: nearer players overlapping the subject box.
      const area = Math.max(1e-6, (_sbox.x1 - _sbox.x0) * (_sbox.y1 - _sbox.y0));
      for (let i = 0; i < s.players.length; i++) {
        const q = s.players[i]!;
        if (q.id === subjectId) continue;
        if (!playerBox(pose, q.pos, _box) || _box.depth >= _sbox.depth - 0.3) continue;
        const ox = Math.min(_box.x1, _sbox.x1) - Math.max(_box.x0, _sbox.x0);
        if (ox <= 0) continue;
        const oy = Math.min(_box.y1, _sbox.y1) - Math.max(_box.y0, _sbox.y0);
        if (oy <= 0) continue;
        occ = Math.max(occ, Math.min(1, (ox * oy) / area));
      }
    }
  } else if (subjectId === 'ball') {
    depth = Math.max(ballDepth, 0.5);
    sx = ballNdcX; sy = ballNdcY;
    subjectVisible = ballInFrame;
    cutScore = inFrame(sx, sy, 0.9) ? 1 : 0.6;
    vPenalty = Math.max(0, Math.abs(sy) - 0.6) * 1.5;
  } else {
    const sp = input.subjectPoint ?? { x: pose.position.x + pose.forward.x * 30, y: pose.position.y + pose.forward.y * 30, z: pose.position.z + pose.forward.z * 30 };
    projectPoint(pose, sp.x, sp.y, sp.z, _p);
    depth = Math.max(_p.depth, 0.5);
    sx = _p.x; sy = _p.y;
    subjectVisible = _p.depth > 0 && inFrame(sx, sy);
  }
  fraction = personFraction(pose, depth);
  const shotSize = shotSizeFromFraction(fraction);

  // --- quality ----------------------------------------------------------------------
  const vfovRad = (pose.fovDeg * Math.PI) / 180;
  const motion = (input.angularSpeed ?? 0) / Math.max(vfovRad, 1e-3); // frame-heights per second
  const steady = 1 / (1 + (motion / 0.9) * (motion / 0.9));
  let quality: number;
  if (!subjectVisible) {
    quality = 0.08 * steady;
  } else {
    const ax = Math.abs(sx);
    const dx = Math.min(ax, Math.abs(ax - 1 / 3)); // centre or a vertical third
    const comp = Math.max(0, 1 - (dx / 0.55) * (dx / 0.55) - vPenalty);
    quality = (0.45 * comp + 0.55 * cutScore) * (0.5 + 0.5 * steady) * (1 - 0.6 * occ);
  }

  return {
    subjectId,
    shotSize,
    ballInFrame,
    focusInFrame,
    visible,
    quality: Math.max(0, Math.min(1, quality)),
  };
}
